import {Box, Button, FormLabel, Heading, Image, Input, Stack, Text} from "@chakra-ui/react";
import {getStorage, ref, uploadBytesResumable} from "firebase/storage";
import React, {ChangeEvent, useContext, useEffect, useMemo, useState} from "react";
import {v4 as uuidv4} from "uuid";
import RichTextEditor from "../../form/RichTextEditor";
import {AppContext, AppContextI} from "../../context/ContextWrapper";
import {AuthContext, AuthContextI} from "../../../auth/FirebaseAuth";
import {firebaseApp} from "../../../firebaseClient";
import {firebaseFunctionsUri} from "../../../graphql/apolloClient";
import {buildAreaCoverImageApiPath, getAreaCoverImagePath} from "../../upload/uploadPaths";

type AreaGeneralTabProps = {
	canEdit: boolean;
	areaId?: string;
	name: string;
	summary: string;
	description: string;
	coverImageUrl?: string;
	creator: string;
	onNameChange: (value: string) => void;
	onSummaryChange: (value: string) => void;
	onDescriptionChange: (value: string) => void;
	onCoverImageChange: (value: string | undefined) => void;
	onCreatorChange: (value: string) => void;
};

const AreaGeneralTab = ({
	canEdit,
	areaId,
	name,
	summary,
	description,
	coverImageUrl,
	creator,
	onNameChange,
	onSummaryChange,
	onDescriptionChange,
	onCoverImageChange,
	onCreatorChange
}: AreaGeneralTabProps) => {
	const appContext: AppContextI = useContext<AppContextI>(AppContext);
	const authContext: AuthContextI = useContext<AuthContextI>(AuthContext);
	const [isUploadingCoverImage, setIsUploadingCoverImage] = useState<boolean>(false);
	const [fileInputKey, setFileInputKey] = useState<number>(0);
	const [authToken, setAuthToken] = useState<string | undefined>(authContext.getToken());

	useEffect(() => {
		const existingToken = authContext.getToken();
		if (existingToken) {
			setAuthToken(existingToken);
			return;
		}
		void authContext.getTokenAsync().then((token) => {
			setAuthToken(token);
		});
	}, [authContext]);

	const coverImagePreviewUrl = useMemo(() => {
		const storedValue = (coverImageUrl || "").trim();
		if (!storedValue) {
			return undefined;
		}
		if (/^https?:\/\//i.test(storedValue)) {
			return storedValue;
		}
		const separator = storedValue.includes("?") ? "&" : "?";
		const authorizationQuery = authToken ? `${separator}authorization=${encodeURIComponent(`Bearer ${authToken}`)}` : "";
		return `${firebaseFunctionsUri}${storedValue}${authorizationQuery}`;
	}, [authToken, coverImageUrl]);

	const handleCoverImageSelected = async (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) {
			return;
		}
		if (!canEdit) {
			setFileInputKey((current) => current + 1);
			return;
		}
		if (!file.type.startsWith("image/")) {
			appContext.showError("Cover image must be an image file");
			setFileInputKey((current) => current + 1);
			return;
		}
		if (!areaId) {
			appContext.showError("Unable to determine space for cover image upload");
			setFileInputKey((current) => current + 1);
			return;
		}

		setIsUploadingCoverImage(true);
		try {
			const extensionIndex = file.name.lastIndexOf(".");
			const extension = extensionIndex >= 0 ? file.name.substring(extensionIndex) : "";
			const fileName = `${uuidv4()}${extension}`;
			const storagePath = getAreaCoverImagePath(areaId);
			const storage = getStorage(firebaseApp);
			const storageRef = ref(storage, `${storagePath}/${fileName}`);
			const uploadTask = uploadBytesResumable(storageRef, file);
			await new Promise<void>((resolve, reject) => {
				uploadTask.on("state_changed", undefined, reject, () => resolve());
			});
			onCoverImageChange(buildAreaCoverImageApiPath(`${storagePath}/${fileName}`));
		} catch (error) {
			console.error(error);
			appContext.showError("Unable to upload cover image");
		} finally {
			setIsUploadingCoverImage(false);
			setFileInputKey((current) => current + 1);
		}
	};

	return (
		<Stack spacing={6}>
			<Box>
				<Heading size={"sm"} mb={2}>Name</Heading>
				<Input
					value={name}
					onChange={(event) => onNameChange(event.target.value)}
					isDisabled={!canEdit}
					placeholder={"Space name"}
					maxW={"520px"}
				/>
			</Box>
			<Box>
				<Heading size={"sm"} mb={2}>Summary</Heading>
				<Input
					value={summary}
					onChange={(event) => onSummaryChange(event.target.value)}
					isDisabled={!canEdit}
					placeholder={"Short summary"}
					maxW={"720px"}
				/>
			</Box>
			<Box>
				<Heading size={"sm"} mb={2}>Description</Heading>
				<RichTextEditor
					value={description}
					onChange={onDescriptionChange}
					placeholder={"Space description"}
					isDisabled={!canEdit}
					minHeight={"220px"}
				/>
			</Box>
			<Box>
				<Heading size={"sm"} mb={2}>Cover image</Heading>
				<Stack spacing={2} maxW={"520px"}>
					<FormLabel marginBottom={0} fontSize={"sm"}>
						Upload an image to represent this space.
					</FormLabel>
					<Input
						key={fileInputKey}
						size={"sm"}
						type={"file"}
						accept={"image/*"}
						onChange={handleCoverImageSelected}
						isDisabled={!canEdit || isUploadingCoverImage}
						padding={1}
					/>
					{isUploadingCoverImage ? (
						<Text fontSize={"sm"} color={"gray.600"}>Uploading cover image...</Text>
					) : null}
					{coverImageUrl ? (
						<Stack spacing={2}>
							<Image
								src={coverImagePreviewUrl}
								alt={`${name || "Space"} cover`}
								maxH={"180px"}
								objectFit={"cover"}
								borderRadius={"md"}
								borderWidth={"1px"}
								borderColor={"gray.200"}
								alignSelf={"flex-start"}
							/>
							<Button
								size={"sm"}
								variant={"outline"}
								width={"fit-content"}
								onClick={() => onCoverImageChange(undefined)}
								isDisabled={!canEdit || isUploadingCoverImage}
							>
								Remove cover image
							</Button>
						</Stack>
					) : (
						<Text fontSize={"sm"} color={"gray.600"}>No cover image uploaded.</Text>
					)}
				</Stack>
			</Box>
			<Box>
				<Heading size={"sm"} mb={2}>Creator</Heading>
				<Input
					value={creator}
					onChange={(event) => onCreatorChange(event.target.value)}
					isDisabled={!canEdit}
					placeholder={"Creator"}
					maxW={"520px"}
				/>
			</Box>
			{!canEdit ? (
				<Text fontSize={"sm"} color={"orange.600"}>
					Space settings can only be updated while editing a draft.
				</Text>
			) : null}
		</Stack>
	);
};

export default AreaGeneralTab;
